import {
  AGGREGATION_METHODS,
  CALCULATION_METHODS,
  EF_BASES,
  FACTOR_GASES,
  FIXED_TAG_GROUPS,
  FREQUENCIES,
  KPI_CATEGORIES,
  KPI_STATUSES,
  QUESTION_TYPES,
} from "../options";
import { kpiTypeOptionsFor, supportsAggregation } from "../helpers";
import { KG_CO2E } from "../../../../../_constants/emissionUnits";
import {
  FACTOR_COLUMNS,
  KPI_COLUMNS,
  SHEET,
  YES_NO,
  formatList,
  formatUnits,
  listsFor,
  optionLabel,
} from "./excelSpec";

const SPARE_ROWS = 500;

const HEADER_FILL = { type: "pattern", pattern: "solid", fgColor: { argb: "FF2F7590" } };
const READ_ONLY_FILL = { type: "pattern", pattern: "solid", fgColor: { argb: "FFEFF5F7" } };

const columnLetter = (index) => {
  let n = index + 1;
  let letters = "";
  while (n > 0) {
    const rem = (n - 1) % 26;
    letters = String.fromCharCode(65 + rem) + letters;
    n = Math.floor((n - 1) / 26);
  }
  return letters;
};

const tagValue = (groups, name, index) =>
  (groups || []).find((group) => group.name === name)?.pairs[index]?.value ?? "";

/**
 * One KPI as a KPIs-sheet row, keyed by column.
 * @param {object} row normalised grid row
 * @param {{ modules: object[], units: object[] }} catalogue
 */
const kpiCells = (row, catalogue) => {
  const kpiTypeOptions = kpiTypeOptionsFor(row.kpiTypes);
  const cells = {
    id: row.questionId,
    title: row.title,
    module: optionLabel(catalogue.modules, row.moduleId),
    type: optionLabel(QUESTION_TYPES, row.type),
    kpiType: formatList(row.kpiTypes.map((type) => optionLabel(kpiTypeOptions, type))),
    frequency: optionLabel(FREQUENCIES, row.frequency),
    aggregation: supportsAggregation(row.type) ? optionLabel(AGGREGATION_METHODS, row.aggregation) : "",
    category: optionLabel(KPI_CATEGORIES, row.category),
    defaultDescription: row.defaultDescription || "",
    yearDescription: row.yearDescription || "",
    readOnly: row.isReadOnly ? YES_NO[0] : YES_NO[1],
    applicableCheck: row.applicableCheck ? YES_NO[0] : YES_NO[1],
    applicableNote: row.applicableNote ? YES_NO[0] : YES_NO[1],
    trigerCheck: row.trigerCheck ? YES_NO[0] : YES_NO[1],
    units: formatUnits(row.units, catalogue.units),
    evidences: formatList(row.evidences),
    industries: formatList((row.industryIds || []).map((id) => optionLabel(catalogue.industries || [], id) || id)),
    frameworks: formatList((row.frameworkIds || []).map((id) => optionLabel(catalogue.frameworks || [], id) || id)),
    sectionMapping: row.sectionMapping || "",
    formula: row.formula || "",
    variables: formatList(row.variables),
    kpiStatus: optionLabel(KPI_STATUSES, row.kpiStatus),
    active: row.active ? YES_NO[0] : YES_NO[1],
  };
  KPI_COLUMNS.filter((c) => c.tagGroup).forEach((c) => {
    cells[c.key] = tagValue(row.tagGroups, c.tagGroup, c.tagIndex);
  });
  return cells;
};

/**
 * A decimal column as a number cell, or blank when the factor leaves it unset.
 *
 * @param {string|null|undefined} text
 * @returns {number|""}
 */
const numberCell = (text) => (String(text ?? "").trim() === "" ? "" : Number(text));

/**
 * @param {import("../helpers").GridFactor} factor
 * @param {object} row the KPI it belongs to
 * @param {{ units: object[], databases: object[] }} catalogue
 */
const factorCells = (factor, row, catalogue) => ({
  factorId: factor.id,
  kpiId: row.questionId,
  kpiTitle: row.title,
  scope: factor.scope,
  country: factor.countryCode,
  unit: optionLabel(catalogue.units, factor.unitId),
  value: factor.value === "" ? "" : Number(factor.value),
  source: optionLabel(catalogue.databases, factor.databaseId) || factor.databaseName || "",
  method: optionLabel(CALCULATION_METHODS, factor.method || "DIRECT"),
  basis: optionLabel(EF_BASES, factor.basis || "GAS_MASS"),
  density: numberCell(factor.density),
  ncv: numberCell(factor.ncv),
  ...Object.fromEntries(FACTOR_GASES.flatMap(({ ef, gwp }) => [[ef, numberCell(factor[ef])], [gwp, numberCell(factor[gwp])]])),
  gwpSource: factor.gwpSource || "",
  delete: "",
});

const fillSheet = (sheet, columns, listRanges, dataRows) => {
  sheet.columns = columns.map((c) => ({ key: c.key, header: c.header, width: c.width }));
  dataRows.forEach((cells) => sheet.addRow(cells));
  const lastRow = dataRows.length + 1 + SPARE_ROWS;
  const header = sheet.getRow(1);
  columns.forEach((c, i) => {
    const cell = header.getCell(i + 1);
    cell.value = c.header;
    cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
    cell.fill = HEADER_FILL;
    cell.alignment = { vertical: "middle", wrapText: true };
  });
  header.height = 30;
  sheet.views = [{ state: "frozen", xSplit: 2, ySplit: 1 }];
  sheet.autoFilter = { from: "A1", to: `${columnLetter(columns.length - 1)}1` };

  columns.forEach((c, i) => {
    const letter = columnLetter(i);
    if (c.readOnly) {
      for (let r = 2; r <= lastRow; r += 1) sheet.getCell(`${letter}${r}`).fill = READ_ONLY_FILL;
      header.getCell(i + 1).note = "Filled in by the system. Leave blank for a new row; do not change it.";
    }
    if (c.list) {
      sheet.dataValidations.add(`${letter}2:${letter}${lastRow}`, {
        type: "list",
        allowBlank: true,
        formulae: [listRanges[c.list]],
        showErrorMessage: !c.open,
        errorStyle: "stop",
        errorTitle: c.header,
        error: `Pick a ${c.header.toLowerCase()} from the list.`,
      });
    }
  });
};

const HELP = [
  "How to use this workbook",
  "",
  "KPIs sheet — one row per KPI for the financial year shown on the grid.",
  "• KPI ID blank = a new KPI. KPI ID filled = update that KPI. Do not change an ID.",
  "• Module, Type, Frequency, Aggregation, Category and the Yes / No columns have dropdowns.",
  "• Supported units: kWh (base); MWh = 1000 — mark one base unit, give the others their factor into it.",
  "• Emission / Energy / Module tags: Module, Category, Sub-category and KPI, in that order.",
  "• KPI type, Industries and Frameworks: several values separated by ; (names are on the Lists sheet).",
  "• Evidence: several values separated by ; — the standard kinds are on the Lists sheet, any other text is kept as typed.",
  "• Default description applies to every financial year; This year description overrides it for this year only.",
  "• Read only applies to tabular KPIs. Table setup and variable unit conversions are edited in the grid.",
  "• Formula and Variables go together: a KPI with a formula needs its variables (Q594; Q595).",
  "• KPI status: Draft or Published.",
  "",
  "Emission factors sheet — one row per factor.",
  "• Factor ID filled = update that factor. Blank = add a new one.",
  "• To remove a factor, set Delete to Yes. Factors not in the sheet are left alone.",
  "• Link a factor to its KPI by KPI ID, or by KPI title for a KPI added in this same file.",
  "• Gas-wise factors: fill CO2 / CH4 / N2O factor and GWP; Value is then calculated; leave it as downloaded or blank, a different number is refused.",
  "• Method Fuel energy (IPCC): quantity × Density (kg per unit) × NCV (TJ/Gg) ÷ 1,000,000 gives TJ; gas factors are kg per TJ.",
  `• Method Direct: gas factors are kg per unit. Gas values 'Already ${KG_CO2E}' (DEFRA breakdown) are summed without GWP.`,
  "",
  "Upload the file from the grid's Excel menu. You see every change before anything is saved.",
];

/**
 * The KPI workbook: KPIs, Emission factors, the Lists behind the dropdowns and
 * a Read me. With no rows it is the blank template.
 *
 * ExcelJS is passed in so the page can load it lazily and tests can use the
 * Node build.
 *
 * @param {typeof import("exceljs")} ExcelJS
 * @param {{ rows: object[], catalogue: { modules: object[], units: object[], databases: object[] }, financialYearLabel?: string }} args
 *   rows are normalised grid rows with `factors` attached
 */
export const buildWorkbook = (ExcelJS, { rows, catalogue, financialYearLabel }) => {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "RIU KPI Grid";
  workbook.created = new Date();

  const kpiSheet = workbook.addWorksheet(SHEET.kpis);
  const factorSheet = workbook.addWorksheet(SHEET.factors);
  const listSheet = workbook.addWorksheet(SHEET.lists);
  const helpSheet = workbook.addWorksheet(SHEET.help);

  const lists = listsFor(catalogue);
  const listRanges = {};
  lists.forEach((list, i) => {
    const letter = columnLetter(i);
    listSheet.getCell(`${letter}1`).value = list.header;
    listSheet.getCell(`${letter}1`).font = { bold: true };
    list.values.forEach((value, r) => {
      listSheet.getCell(`${letter}${r + 2}`).value = value;
    });
    listSheet.getColumn(i + 1).width = 24;
    listRanges[list.name] = `'${SHEET.lists}'!$${letter}$2:$${letter}$${Math.max(list.values.length + 1, 2)}`;
  });

  const factorRows = rows.flatMap((row) => (row.factors || []).map((factor) => ({ factor, row })));

  fillSheet(kpiSheet, KPI_COLUMNS, listRanges, rows.map((row) => kpiCells(row, catalogue)));
  fillSheet(
    factorSheet,
    FACTOR_COLUMNS,
    listRanges,
    factorRows.map(({ factor, row }) => factorCells(factor, row, catalogue)),
  );

  const tagFills = ["FFE4F0F5", "FFFDF4E3", "FFE3F4F0"];
  KPI_COLUMNS.forEach((c, i) => {
    if (!c.tagGroup) return;
    const fill = tagFills[FIXED_TAG_GROUPS.findIndex((g) => g.name === c.tagGroup) % tagFills.length];
    const cell = kpiSheet.getRow(1).getCell(i + 1);
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: fill } };
    cell.font = { bold: true, color: { argb: "FF13232A" } };
  });

  helpSheet.getColumn(1).width = 110;
  [financialYearLabel ? `Financial year: ${financialYearLabel}` : null, ...HELP]
    .filter((line) => line !== null)
    .forEach((line, i) => {
      const cell = helpSheet.getCell(`A${i + 1}`);
      cell.value = line;
      if (i === 0) cell.font = { bold: true, size: 13 };
    });

  return workbook;
};
